"use client";

// Phase 2 — TerminalHero. Pure HTML+CSS terminal pinned to the viewport so
// it follows the user as they scroll the hero. The wrapper is a motion.div
// so the parent <AnimatePresence> can crossfade it with TerminalFragments
// when the scene state moves to "fragmented" (Phase 5).

import { motion } from "motion/react";
import { useEffect, useState } from "react";

type Token = { t: string; c?: string };
type Line = Token[];

// Tokenized lines = no need for a separate syntax highlighter. Each token
// renders as a span with its colour class.
const SCRIPT: Line[] = [
  [{ t: "$ ", c: "text-cyan-300" }, { t: "npm run build:dream", c: "text-zinc-100" }],
  [{ t: "› compiling magic …… ", c: "text-zinc-500" }],
  [
    { t: "const ", c: "text-violet-300" },
    { t: "kessem", c: "text-cyan-300" },
    { t: " = {", c: "text-zinc-200" },
  ],
  [
    { t: "  mission", c: "text-zinc-300" },
    { t: ": ", c: "text-zinc-400" },
    { t: "'create magic'", c: "text-emerald-300" },
    { t: ",", c: "text-zinc-400" },
  ],
  [
    { t: "  delivery", c: "text-zinc-300" },
    { t: ": ", c: "text-zinc-400" },
    { t: "'7 days'", c: "text-emerald-300" },
    { t: ",", c: "text-zinc-400" },
  ],
  [{ t: "};", c: "text-zinc-200" }],
  [
    { t: "await ", c: "text-violet-300" },
    { t: "deploy", c: "text-cyan-300" },
    { t: "(", c: "text-zinc-400" },
    { t: "yourDream", c: "text-zinc-100" },
    { t: ");", c: "text-zinc-400" },
  ],
  [{ t: "// ✓ shipped to production", c: "text-emerald-300" }],
];

// Total characters per line (used by typing loop)
const LINE_LENGTHS = SCRIPT.map((line) =>
  line.reduce((s, tok) => s + tok.t.length, 0)
);

const CHAR_INTERVAL = 22;
const LINE_PAUSE = 260;

function useTypingScript() {
  const [lineIdx, setLineIdx] = useState(0);
  const [colIdx, setColIdx] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      // Reduced-motion: jump straight to the final state
      setLineIdx(SCRIPT.length);
      setColIdx(0);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    function step() {
      if (cancelled) return;
      const lineLen = LINE_LENGTHS[lineIdx];
      if (lineLen === undefined) {
        // End of script — stop here. The cursor keeps blinking on the last
        // line; no more typing, no restart.
        return;
      }
      if (colIdx < lineLen) {
        timer = setTimeout(() => {
          if (cancelled) return;
          setColIdx((c) => c + 1);
        }, CHAR_INTERVAL);
      } else {
        timer = setTimeout(() => {
          if (cancelled) return;
          setLineIdx((i) => i + 1);
          setColIdx(0);
        }, LINE_PAUSE);
      }
    }
    step();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [lineIdx, colIdx]);

  return { lineIdx, colIdx };
}

// Render a line up to `maxChars` characters, walking through its tokens.
function renderLineUpTo(line: Line, maxChars: number) {
  let remaining = maxChars;
  const out: React.ReactNode[] = [];
  for (let i = 0; i < line.length; i++) {
    if (remaining <= 0) break;
    const tok = line[i];
    const slice = tok.t.slice(0, remaining);
    remaining -= slice.length;
    out.push(
      <span key={i} className={tok.c}>
        {slice}
      </span>
    );
  }
  return out;
}

// Tracks the pointer and lerps a small parallax offset (in pixels) for the
// terminal panel — the panel slowly drifts towards the cursor so it always
// feels alive and 3D, without jitter.
function useMouseParallax(strength = 18) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(pointer: fine)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    const target = { x: 0, y: 0 };
    let current = { x: 0, y: 0 };
    const onMove = (e: MouseEvent) => {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      target.x = ((e.clientX - cx) / cx) * strength;
      target.y = ((e.clientY - cy) / cy) * strength;
    };
    const tick = () => {
      current = {
        x: current.x + (target.x - current.x) * 0.05,
        y: current.y + (target.y - current.y) * 0.05,
      };
      setOffset({ x: current.x, y: current.y });
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, [strength]);
  return offset;
}

export function TerminalHero() {
  const { lineIdx, colIdx } = useTypingScript();
  const offset = useMouseParallax(20);

  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 1, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.02 }}
      transition={{
        scale: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
        // Opacité INSTANTANÉE — le mix-blend-mode des titres data-blend
        // doit voir la nouvelle couleur de fond sans aucun crossfade.
        opacity: { duration: 0 },
      }}
      className="pointer-events-none fixed inset-0 hidden md:flex items-center justify-center"
      style={{
        perspective: 1400,
        // Pas de zIndex : il créerait un stacking context qui empêcherait
        // mix-blend-mode des titres data-blend de voir la couleur du terminal.
        transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
      }}
    >
      <div
        className="relative will-change-transform"
        style={{
          transform: "rotateX(8deg) rotateY(0deg)",
          transformStyle: "preserve-3d",
          animation: "term-float 8s ease-in-out infinite",
        }}
      >
        {/* Cyan glow halo behind the panel for depth */}
        <div
          aria-hidden
          className="absolute -inset-10 -z-[1] rounded-[28px] blur-3xl opacity-50"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(34,211,238,0.55), rgba(139,92,246,0.30) 45%, transparent 70%)",
          }}
        />
        {/* Terminal panel — solid black. The hero title sitting on top uses
           mix-blend-mode: difference so its colour inverts where it crosses
           the panel, staying readable everywhere. */}
        <div
          className="relative w-[clamp(420px,42vw,620px)] overflow-hidden rounded-2xl border border-cyan-300/35 bg-black text-left font-mono text-[clamp(11px,1vw,14px)] leading-[1.55] shadow-[0_36px_84px_-22px_rgba(34,211,238,0.55),0_0_0_1px_rgba(34,211,238,0.24),inset_0_0_0_1px_rgba(255,255,255,0.05)]"
        >
          {/* Title bar */}
          <div className="flex items-center justify-between border-b border-white/5 bg-black/40 px-3 py-2">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <span className="size-2 rounded-full bg-rose-400/85" />
                <span className="size-2 rounded-full bg-amber-300/85" />
                <span className="size-2 rounded-full bg-emerald-300/85" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                kessem.studio — main
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-cyan-300/70">
              / N°001
            </span>
          </div>

          {/* Code body */}
          <div className="relative flex flex-col gap-[1px] px-5 py-5 text-zinc-200">
            {SCRIPT.slice(0, lineIdx).map((line, i) => {
              const isFinalLine = lineIdx >= SCRIPT.length && i === SCRIPT.length - 1;
              return (
                <div key={`done-${i}`} className="min-h-[1em] whitespace-pre">
                  {renderLineUpTo(line, LINE_LENGTHS[i])}
                  {isFinalLine && (
                    <span
                      aria-hidden
                      className="ms-[1px] inline-block h-[1em] w-[7px] -mb-[2px] align-text-bottom bg-cyan-300"
                      style={{ animation: "term-cursor 1s steps(2) infinite" }}
                    />
                  )}
                </div>
              );
            })}
            {SCRIPT[lineIdx] && (
              <div key={`live-${lineIdx}`} className="min-h-[1em] whitespace-pre">
                {renderLineUpTo(SCRIPT[lineIdx], colIdx)}
                <span
                  aria-hidden
                  className="ms-[1px] inline-block h-[1em] w-[7px] -mb-[2px] align-text-bottom bg-cyan-300"
                  style={{ animation: "term-cursor 1s steps(2) infinite" }}
                />
              </div>
            )}
          </div>

          {/* Inner top sheen */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-12"
            style={{
              background:
                "linear-gradient(180deg, rgba(34,211,238,0.10), transparent)",
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes term-float {
          0%, 100% { transform: translateY(-4px) rotateX(8deg); }
          50%      { transform: translateY(4px)  rotateX(7deg); }
        }
        @keyframes term-cursor { 0%,49%{opacity:1} 50%,100%{opacity:0} }
        @media (prefers-reduced-motion: reduce) {
          [aria-hidden] [style*="term-float"],
          [aria-hidden] [style*="term-cursor"] {
            animation: none !important;
          }
        }
      `}</style>
    </motion.div>
  );
}
